/**
 * Serviço de Autenticação
 * Comunicação com o backend NestJS para login, registro e gerenciamento de tokens
 */

import { env } from '../config/env';
import i18n from '../config/i18n';
import { isTokenExpired, isValidJWTFormat } from '../utils/jwtUtils';
import { courseHeader, getActiveCourse } from './courseContext';
import { formatEndDate } from '../utils/accessDate';
import { forgetAppBrowserData } from '../utils/browserData';

const TOKEN_KEY = 'neurodidatica_token';
const USER_KEY = 'neurodidatica_user';
const SESSION_EXPIRED_EVENT = 'auth:session-expired';

function notifySessionExpired(): void {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

export { SESSION_EXPIRED_EVENT };

/**
 * Acesso negado pelo backend sem curso aberto (conta bloqueada ou prazo vencido
 * em todos os cursos): a sessão termina e a tela de login diz o motivo (TEXTO 6).
 */
export const ACCESS_DENIED_EVENT = 'auth:access-denied';

/**
 * Acesso negado DENTRO de um curso. A pessoa pode ter outro curso em dia, então
 * a sessão continua: a casca do curso volta para a vitrine, que mostra o aviso
 * no card daquele curso.
 */
export const COURSE_ACCESS_DENIED_EVENT = 'course:access-denied';

export type AccessDeniedCode = 'ACCESS_EXPIRED' | 'ACCOUNT_BLOCKED';

export interface AccessDeniedDetail {
  code: AccessDeniedCode;
  /** Último dia de acesso ('YYYY-MM-DD'), no vencimento. */
  accessEndDate: string | null;
}

export interface CourseAccessDeniedDetail extends AccessDeniedDetail {
  topicId: number;
}

/** Texto para o aluno, no idioma dele. A `message` do backend vem sempre em português. */
export function accessDeniedMessage(detail: AccessDeniedDetail): string {
  if (detail.code === 'ACCOUNT_BLOCKED') return i18n.t('access.blocked');
  return detail.accessEndDate
    ? i18n.t('access.expiredOn', { date: formatEndDate(detail.accessEndDate) })
    : i18n.t('access.expired');
}

export class AccessDeniedError extends Error {
  constructor(readonly detail: AccessDeniedDetail) {
    super(accessDeniedMessage(detail));
    this.name = 'AccessDeniedError';
  }
}

/** O corpo é um 403 de acesso negado? Os demais 403 (admin, papel, Preceptor) não são. */
export function readAccessDenied(body: unknown): AccessDeniedDetail | null {
  if (!body || typeof body !== 'object') return null;
  const { code, accessEndDate } = body as { code?: unknown; accessEndDate?: unknown };
  if (code !== 'ACCESS_EXPIRED' && code !== 'ACCOUNT_BLOCKED') return null;
  return {
    code,
    accessEndDate: typeof accessEndDate === 'string' ? accessEndDate : null,
  };
}

export interface User {
  /**
   * Id da matrícula com que a sessão começou. Continua sendo o que
   * `switch-course` espera, enquanto essa rota existir.
   */
  id: string;
  email: string;
  name: string;
  /**
   * Curso em que a sessão começou. Deixou de mandar em qualquer coisa: quem diz
   * o curso da tela é a URL, e quem diz o curso da requisição é o header.
   */
  topicId: number;
  isAdmin: boolean;
  preceptorEnabled?: boolean;
}

export interface AuthResponse {
  access_token: string;
  user: User;
}

export interface CourseOption {
  /** Id da matrícula. O nome ficou de quando cada curso era uma conta. */
  userId: string;
  enrollmentId?: string;
  topicId: number;
  topicName: string;
  isBlocked: boolean;
  status?: 'ativa' | 'suspensa' | 'cancelada' | 'expirada';
  /** Papel e acesso administrativo NESTE curso. */
  role?: string;
  isAdmin?: boolean;
  expiresAt?: string | null;
  /** Prazo vencido (só com o bloqueio por vencimento ligado). */
  accessExpired?: boolean;
  /** Último dia de acesso; null enquanto o aluno ainda não vê a data. */
  accessEndDate?: string | null;
}

/**
 * Resposta do login quando ele ainda parava numa tela de seleção.
 *
 * @deprecated O servidor novo nunca mais a devolve. Continua tipada porque o
 * app pode estar falando com um backend anterior ao deploy — e nesse caso o
 * AuthContext a resolve sozinho, sem mostrar tela nenhuma.
 */
export interface CourseSelectionResponse {
  requires_course_selection: true;
  selection_token: string;
  courses: CourseOption[];
}

export type LoginResponse = AuthResponse | CourseSelectionResponse;

export interface LoginCredentials {
  email: string;
  password: string;
  /**
   * Curso pretendido, quando o slug já está na URL. A sessão começa nele; sem
   * ele, começa na primeira matrícula ativa. Em nenhum dos casos há tela de
   * seleção: o curso de cada requisição vem do header, não da sessão.
   */
  topicId?: number;
}

export function isCourseSelectionResponse(
  response: LoginResponse,
): response is CourseSelectionResponse {
  return (response as CourseSelectionResponse).requires_course_selection === true;
}

export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  try {
    const response = await fetch(`${env.apiBaseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const denied = readAccessDenied(error);
      if (denied) throw new AccessDeniedError(denied);
      throw new Error(error.message || i18n.t('auth.loginError'));
    }

    const data: LoginResponse = await response.json();

    // Only persist credentials when we have a final token (single course)
    if (!isCourseSelectionResponse(data)) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    }

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

export async function selectCourse(
  selectionToken: string,
  userId: string,
): Promise<AuthResponse> {
  try {
    const response = await fetch(`${env.apiBaseUrl}/auth/select-course`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ selectionToken, userId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const denied = readAccessDenied(error);
      if (denied) throw new AccessDeniedError(denied);
      throw new Error(error.message || i18n.t('auth.loginError'));
    }

    const data: AuthResponse = await response.json();

    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

/**
 * Cursos da conta logada (uma matrícula por curso, mesmo e-mail).
 * Usado pela home da plataforma para saber a quais cursos o usuário tem acesso.
 */
export async function getMyCourses(): Promise<CourseOption[]> {
  const response = await authenticatedFetch('/auth/my-courses');

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || i18n.t('platform.loadError'));
  }

  return response.json();
}

/**
 * Troca o curso ativo sem novo login, reemitindo o token para outra matrícula.
 *
 * Não usa authenticatedFetch de propósito: lá todo 401 dispara logout +
 * SESSION_EXPIRED. Aqui o 401 significa "este curso não pertence à sua conta",
 * um erro de negócio — deslogar o usuário por causa dele seria desproporcional
 * e mascararia a causa real.
 */
export async function switchCourse(userId: string): Promise<AuthResponse> {
  const token = getStoredToken();

  if (!token) {
    throw new Error(i18n.t('auth.userNotAuthenticated'));
  }

  // Token expirado continua sendo sessão expirada de verdade.
  if (isTokenExpired(token)) {
    logout();
    notifySessionExpired();
    throw new Error(i18n.t('auth.sessionExpired'));
  }

  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}/auth/switch-course`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ userId }),
    });
  } catch {
    throw new Error(i18n.t('common.serverConnectionError'));
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || i18n.t('platform.switchError'));
  }

  const data: AuthResponse = await response.json();

  // Regravar o USER_KEY é obrigatório: getStoredUser() é a fonte do estado após
  // um reload, e sem isso o F5 voltaria ao curso anterior com o token novo.
  localStorage.setItem(TOKEN_KEY, data.access_token);
  localStorage.setItem(USER_KEY, JSON.stringify(data.user));

  return data;
}

export function logout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // A Política (cl. 2.8) promete que o que o app guarda no navegador sai no
  // logout — e a última aula ou o último filtro de uma conta não devem aparecer
  // para a próxima pessoa no mesmo aparelho.
  forgetAppBrowserData();
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): User | null {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  const token = getStoredToken();
  
  if (!token) {
    return false;
  }
  
  if (!isValidJWTFormat(token)) {
    console.warn('Token armazenado tem formato inválido');
    logout();
    return false;
  }
  
  if (isTokenExpired(token)) {
    console.log('Token expirado, fazendo logout automático');
    logout();
    return false;
  }
  
  return true;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ message: string }> {
  const response = await authenticatedFetch('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || i18n.t('auth.changePasswordError'));
  }

  return response.json();
}

export async function authenticatedFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getStoredToken();
  
  if (!token) {
    throw new Error(i18n.t('auth.userNotAuthenticated'));
  }
  
  if (isTokenExpired(token)) {
    console.log('Token expirado ao tentar fazer requisição, fazendo logout');
    logout();
    notifySessionExpired();
    throw new Error(i18n.t('auth.sessionExpired'));
  }
  
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'X-Requested-With': 'XMLHttpRequest',
    // O curso da requisição. Antes ele estava embutido na matrícula que o token
    // apontava, e trocar de curso exigia um token novo; agora vai aqui, e o
    // servidor confere a matrícula.
    ...courseHeader(),
    ...options.headers,
  };
  
  try {
    const response = await fetch(`${env.apiBaseUrl}${endpoint}`, {
      ...options,
      headers,
    });
    
    if (response.status === 401) {
      console.log('Erro 401: token inválido ou expirado, fazendo logout');
      logout();
      notifySessionExpired();
      throw new Error(i18n.t('auth.sessionInvalid'));
    }

    // Bloqueio ou vencimento: o backend confere a cada requisição. `clone()`
    // porque, nos outros 403, quem chamou ainda lê o corpo.
    if (response.status === 403) {
      const denied = readAccessDenied(await response.clone().json().catch(() => null));
      if (denied) {
        const topicId = getActiveCourse();
        if (topicId != null) {
          // Dentro de um curso, perde-se o curso, não a conta: a pessoa pode ter
          // outro em dia.
          window.dispatchEvent(
            new CustomEvent<CourseAccessDeniedDetail>(COURSE_ACCESS_DENIED_EVENT, {
              detail: { ...denied, topicId },
            }),
          );
        } else {
          logout();
          window.dispatchEvent(new CustomEvent<AccessDeniedDetail>(ACCESS_DENIED_EVENT, { detail: denied }));
        }
        throw new AccessDeniedError(denied);
      }
    }
    
    return response;
  } catch (error) {
    throw error;
  }
}
